import { X } from "lucide-react";
import type { DiscoveryFilterChip } from "@/components/home/UnifiedDiscoveryBar";

interface ListEndNoteProps {
  /** The active filters, as the discovery bar shows them */
  chips: DiscoveryFilterChip[];
  /** Clears the search and every filter */
  onShowAll: () => void;
  /** Called after a filter was removed here, so the page can bring the new list into view */
  onFiltersChange: () => void;
}

/**
 * The end of the event list. With filters on, it names them and offers ways to more events,
 * so a short filtered list isn't mistaken for the whole calendar.
 */
export function ListEndNote({ chips, onShowAll, onFiltersChange }: ListEndNoteProps) {
  if (chips.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="text-all-loaded">
        All events loaded
      </p>
    );
  }

  const canRemoveOne = chips.some((chip) => Boolean(chip.onRemove));

  return (
    // Padding and gaps in px so the large phone text sizes don't crowd out the labels
    <div
      className="mx-auto w-full max-w-xl rounded-[22px] border border-[#cfe1d5] bg-[#fffef9] px-[16px] py-[16px] text-left shadow-[0_8px_26px_rgba(61,48,29,0.05)] md:px-[20px]"
      data-testid="list-end-filters"
    >
      <p className="text-sm font-semibold text-[#292721]">That&apos;s all the events for:</p>
      <div className="mt-[8px] flex flex-wrap items-center gap-[8px]">
        {chips.map((chip) => {
          const { onRemove } = chip;
          if (!onRemove) {
            // A town or category page itself; "Show all events" leaves it
            return (
              <span
                key={chip.id}
                className="inline-flex min-h-[32px] items-center rounded-full border border-[#bfdbc9] bg-[#edf6f0] px-[12px] py-[4px] text-xs font-semibold leading-tight text-[#185e49]"
              >
                {chip.label}
              </span>
            );
          }

          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => {
                onRemove();
                onFiltersChange();
              }}
              className="inline-flex min-h-[44px] items-center gap-[6px] rounded-full border border-[#1D9E75] bg-[#e5f3ec] px-[12px] py-[6px] text-left text-xs font-semibold leading-tight text-[#105b45] transition-colors hover:bg-[#d8eee2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
              aria-label={`Remove ${chip.label} filter`}
              data-testid={`list-end-chip-${chip.id}`}
            >
              <span>{chip.label}</span>
              <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            </button>
          );
        })}
      </div>
      {canRemoveOne && (
        <p className="mt-[12px] text-sm text-[#5f5a52]">Remove a filter to see more events.</p>
      )}
      <button
        type="button"
        onClick={() => {
          onShowAll();
          onFiltersChange();
        }}
        className="mt-[12px] inline-flex min-h-[44px] w-full items-center justify-center rounded-full bg-[#16795e] px-[18px] py-[8px] text-center text-sm font-semibold leading-tight text-white transition-colors hover:bg-[#105b45] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2 sm:w-auto"
        data-testid="list-end-show-all"
      >
        Show all events
      </button>
    </div>
  );
}
